import type { ChatMessage } from './types';

// OS-level notification for a chat message the user can't currently see. The chime
// only helps someone near their speakers; an agent who has switched to Epic or
// another tab while waiting in the queue misses the reply entirely (WIPE-1832).
//
// Android's WebView and older Safari have no Notification API, so every entry
// point here is a no-op without it.

const BODY_LIMIT = 200;

function supported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

// Asks once, and only from a click: Safari and Firefox ignore a permission request
// that isn't tied to a user gesture, and Chrome demotes sites that prompt unbidden.
// Starting a chat is that gesture, and the moment the user knows why we're asking.
export function requestNotificationPermission(): void {
  if (!supported() || Notification.permission !== 'default') return;
  try {
    void Promise.resolve(Notification.requestPermission()).catch(() => {});
  } catch {
    // Old Safari's callback-only signature throws on a missing argument.
  }
}

// True when this window isn't showing the user anything: another tab, another app,
// or a minimized window.
export function windowIsUnattended(): boolean {
  return document.visibilityState === 'hidden' || !document.hasFocus();
}

export function showMessageNotification(
  message: ChatMessage,
  chatTitle: string,
  onClick?: () => void,
): void {
  if (!supported() || Notification.permission !== 'granted') return;
  try {
    const body = message.content.length > BODY_LIMIT
      ? `${message.content.slice(0, BODY_LIMIT - 1)}…`
      : message.content;
    const notification = new Notification(`${message.sender} · ${chatTitle}`, {
      body,
      icon: '/favicon.svg',
      // No tag: Chrome swaps a same-tag notification in silently, so the second reply
      // in a row would update the notification centre without alerting anyone.
    });
    notification.onclick = () => {
      window.focus();
      onClick?.();
      notification.close();
    };
  } catch {
    // Chrome on Android throws from the constructor (it wants a service worker).
    // Notifications are a nicety; never let them break message rendering.
  }
}
